/**
 * @fileoverview Location widget — lat/lng + optional address + accuracy.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Phase 3 renders the canonical lat/lng JSON + an accessible textual
 *   fallback. Phase 4 swaps the placeholder for a real Mapbox picker when
 *   the host injects the picker dependency.
 * - G-025 — mobile-pwa lacked this widget entirely; this implementation
 *   delivers it for all four surfaces.
 *
 * @component LocationField
 *
 * @contract LocationValue
 * Purpose: canonical persisted shape — `{ lat, lng, accuracyM?, address? }`.
 */
import { useState } from 'react';
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';
import { useIsOffline } from '../contracts/rendererContext';
import { captureCurrentLocation } from '../utils/captureLocation';

export interface LocationValue {
  lat: number;
  lng: number;
  accuracyM?: number;
  address?: string;
}

export function LocationField(props: BaseWidgetProps<LocationValue | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as { requiresAccuracy?: 'high' | 'medium' | 'low'; allowManualEntry?: boolean; reverseGeocode?: boolean };
  const isOffline = useIsOffline();
  const disabled = !!resolved?.disabled;

  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);

  const accuracyThreshold = extras.requiresAccuracy === 'high' ? 10 : extras.requiresAccuracy === 'medium' ? 50 : 100;
  const accuracyOk = value?.accuracyM == null || value.accuracyM <= accuracyThreshold;

  const handleCaptureLocation = async () => {
    setCapturing(true);
    setCaptureError(null);

    try {
      const location = await captureCurrentLocation();

      // Note: Reverse geocoding (converting lat/lng to address) is not
      // implemented in the renderer. Host applications can implement this
      // by injecting a geocoding service via context if needed. For now,
      // we preserve any existing address value.
      onChange({
        ...location,
        address: value?.address,
      });
    } catch (error: any) {
      const errorMessage = error.code === 'PERMISSION_DENIED'
        ? 'Location permission denied. Please enable location access in your browser settings.'
        : error.code === 'TIMEOUT'
        ? 'Location request timed out. Please try again.'
        : error.code === 'NOT_SUPPORTED'
        ? 'Geolocation is not supported by this browser.'
        : 'Failed to capture location. Please try again.';

      setCaptureError(errorMessage);
      console.error('[LocationField] Capture failed:', error);
    } finally {
      setCapturing(false);
    }
  };

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="location"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={errorMessage}
      requiredUnmet={requiredUnmet}
    >
      <div className="flex flex-col gap-2" aria-label="Property location selector" data-reverse-geocode={extras.reverseGeocode ? 'true' : 'false'}>
        {isOffline && (
          <span data-pill="offline" role="status" className="self-start inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
            offline — using device GPS only
          </span>
        )}
        <button
          id={field.id}
          type="button"
          data-action="capture-location"
          disabled={disabled || capturing}
          onClick={handleCaptureLocation}
          aria-required={!!(field.required || resolved?.required)}
          className="px-3 py-2 text-sm border border-dashed border-gray-300 rounded-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          {capturing ? '📍 Capturing...' : value ? `📍 ${value.lat.toFixed(5)}, ${value.lng.toFixed(5)}` : 'Capture location'}
        </button>
        {value?.accuracyM != null && !accuracyOk && (
          <p role="alert" className="text-xs text-amber-600">
            Accuracy {value.accuracyM}m exceeds {extras.requiresAccuracy ?? 'low'} threshold ({accuracyThreshold}m).
          </p>
        )}
        {captureError && (
          <p role="alert" className="text-xs text-red-600">
            {captureError}
          </p>
        )}
        {value?.address && (
          <p className="text-xs text-gray-600">
            📍 {value.address}
          </p>
        )}
        {extras.allowManualEntry && (
          <input
            type="text"
            placeholder="Address (manual)"
            value={value?.address ?? ''}
            onChange={(e) => onChange({ lat: value?.lat ?? 0, lng: value?.lng ?? 0, accuracyM: value?.accuracyM, address: e.target.value })}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm outline-hidden focus:ring-2 focus:ring-blue-300 disabled:opacity-50"
          />
        )}
      </div>
    </FieldWrapper>
  );
}
